/**
 * Training Relevance Scorer — Phase 2 + Phase 3 + Phase 4 + Phase 5
 *
 * Pure client-side scoring layer that re-ranks Phase 1 candidate artifacts.
 *
 * Phase 2: Keyword/category scoring using ticket text context and artifact `keywords`.
 * Phase 3: Engagement boost (small additive signal from interaction data).
 * Phase 4: Pensieve boost (behavioral observation signal).
 * Phase 5: Impact boost (engagement depth + pensieve strength refinement, max +1).
 *          Semantic stub (returns 0, interface ready for future).
 *
 * All candidate artifacts already passed job-based eligibility in Phase 1.
 * This module only re-ranks within that set.
 *
 * DESIGN PRINCIPLE: "Completion" = engagement depth, NOT diagnostic success.
 * This system guides attention, never suggests repairs or likely causes.
 */

import type { TrainingArtifact } from '@/types/training';

// =============================================
// TYPES
// =============================================

export interface TicketContext {
  jobNames: string[];
  jobCategories: string[];
  ticketNotes: string | null;
  customerRequestNotes: string | null;
  /** Optional vehicle YMME for fitment scoring */
  vehicleYear?: number | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleEngine?: string | null;
}

/**
 * Fitment data row from training_artifact_fitment
 */
export interface FitmentRow {
  artifact_id: string;
  year: number | null;
  make: string | null;
  model: string | null;
  engine: string | null;
  boost: number | null;
}

/**
 * Engagement stats per artifact — aggregated from training_effectiveness_events.
 * "completion" here means engagement depth, NOT diagnostic success.
 */
export interface EngagementStats {
  uniqueOpeners: number;
  completionCount: number;
  lastUsedAt: string | null;
}

/**
 * Pensieve stats per artifact — aggregated from training_effect_signals.
 * These are behavioral observations, NOT success indicators.
 * "positive" means "training appears to support forward diagnostic progress".
 */
export interface PensieveStats {
  positiveSignals: number;  // count of positive-direction rows
  totalSamples: number;     // sum of sample_size across all signal types
  avgConfidence: number;    // average confidence_weight
}

export interface ScoreBreakdown {
  category: number;     // Phase 2 (0-2)
  keyword: number;      // Phase 2 (0-5)
  engagement: number;   // Phase 3 (0-2) — engagement depth signal
  pensieve: number;     // Phase 4 (0-2) — behavioral observation signal
  impact: number;       // Phase 5 (0-1) — engagement depth + pensieve strength refinement
  fitment: number;      // Phase 6 (0-10) — vehicle YMME fitment boost
  semantic: number;     // Stub (always 0 until embedding infra)
  total: number;

  // ── CORE-AH: the composition of `impact`, carried rather than inferred ──
  //
  // `impact` is the one blended field: half of it can come from Core-owned
  // engagement depth and half from Catalyst pensieve strength, and the sum
  // does not say which. The displayed explanation must separate Core basis
  // from learned enrichment, and it cannot do that from `impact` alone.
  //
  // These two fields report the split the scorer ALREADY computes. They are
  // provenance, not score: `impactCore + impactLearned === impact`, and
  // NEITHER is added into `total`. Ranking, ordering and every numeric value
  // are unchanged by their presence.
  //
  // Optional so that hand-built breakdowns in older suites still satisfy the
  // type. A consumer that finds them ABSENT alongside a positive `impact`
  // knows only that the composition was not recorded — it must not guess.
  // `scoreAndSort` always populates them.
  impactCore?: number;
  impactLearned?: number;
}

// =============================================
// TEXT NORMALIZATION (shared pattern from diagnosticComplaintMatcher)
// =============================================

function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Word-boundary matching for single words, substring for multi-word phrases.
 * Same logic as diagnosticComplaintMatcher.phraseMatchesText.
 */
function tokenMatchesText(token: string, normalizedText: string): boolean {
  const isMultiWord = token.includes(' ');
  if (isMultiWord) {
    return normalizedText.includes(token);
  }
  return new RegExp(`\\b${token}\\b`, 'i').test(normalizedText);
}

// =============================================
// PHASE 2 SCORING — Context (category + keyword)
// =============================================

const CATEGORY_MATCH_POINTS = 2;
const KEYWORD_MATCH_CAP = 5;

function scoreContext(artifact: TrainingArtifact, context: TicketContext): { category: number; keyword: number } {
  const artifactKeywords = artifact.keywords;

  if (!artifactKeywords || artifactKeywords.length === 0) {
    return { category: 0, keyword: 0 };
  }

  const normalizedKeywords = artifactKeywords.map(k => normalizeText(k));

  // --- Category match (0 or 2) ---
  let categoryScore = 0;
  const normalizedCategories = context.jobCategories
    .filter(Boolean)
    .map(c => normalizeText(c));

  if (normalizedCategories.length > 0) {
    const hasMatch = normalizedKeywords.some(kw =>
      normalizedCategories.some(cat => tokenMatchesText(cat, kw) || tokenMatchesText(kw, cat))
    );
    if (hasMatch) categoryScore = CATEGORY_MATCH_POINTS;
  }

  // --- Keyword match (0–5, capped) ---
  const contextParts: string[] = [];
  if (context.ticketNotes) contextParts.push(context.ticketNotes);
  if (context.customerRequestNotes) contextParts.push(context.customerRequestNotes);
  context.jobNames.filter(Boolean).forEach(n => contextParts.push(n));

  let keywordScore = 0;

  if (contextParts.length > 0) {
    const normalizedContext = normalizeText(contextParts.join(' '));

    for (const kw of normalizedKeywords) {
      if (keywordScore >= KEYWORD_MATCH_CAP) break;
      if (tokenMatchesText(kw, normalizedContext)) {
        keywordScore += 1;
      }
    }
  }

  return { category: categoryScore, keyword: keywordScore };
}

// =============================================
// PHASE 3 SCORING — Engagement boost
// =============================================

const ENGAGEMENT_BOOST_CAP = 2;
const UNIQUE_OPENER_THRESHOLD = 3;

/**
 * Calculate engagement boost from interaction data.
 * This is an ENGAGEMENT DEPTH signal — not an indicator of diagnostic success.
 * Max +2, capped. Small influence by design.
 */
function scoreEngagement(stats: EngagementStats | undefined): number {
  if (!stats) return 0;

  let boost = 0;

  // +1 if multiple distinct technicians opened this artifact
  if (stats.uniqueOpeners >= UNIQUE_OPENER_THRESHOLD) {
    boost += 1;
  }

  // +1 if at least one technician reached completion depth
  // "Completion" = engagement depth, NOT "this helped fix cars"
  if (stats.completionCount >= 1) {
    boost += 1;
  }

  return Math.min(boost, ENGAGEMENT_BOOST_CAP);
}

// =============================================
// PHASE 4 SCORING — Pensieve (behavioral observation)
// =============================================

const PENSIEVE_BOOST_CAP = 2;
const PENSIEVE_MIN_SAMPLES = 5;
const PENSIEVE_MIN_POSITIVE_SIGNALS = 2;

/**
 * Calculate Pensieve boost from behavioral observation data.
 *
 * This is a BEHAVIORAL OBSERVATION signal — it reflects whether technicians
 * continued diagnostic work after engaging with training.
 *
 * "continued_work" is intentionally not a separate signal — it is represented
 * by the union of notes_added, ticket_progressed, and additional_training.
 *
 * Max +2, capped. Small influence by design — never dominates Phase 2 context score.
 */
function scorePensieve(stats: PensieveStats | undefined): number {
  if (!stats) return 0;

  let boost = 0;

  // +1 if sufficient observation with positive trend
  if (stats.totalSamples >= PENSIEVE_MIN_SAMPLES && stats.avgConfidence > 0) {
    boost += 1;
  }

  // +1 if multiple positive behavioral signals observed
  if (stats.positiveSignals >= PENSIEVE_MIN_POSITIVE_SIGNALS) {
    boost += 1;
  }

  return Math.min(boost, PENSIEVE_BOOST_CAP);
}

/**
 * Check if an artifact qualifies for "Supports diagnostic workflow" badge.
 * Requires sufficient observation volume AND multiple positive signals.
 */
export function hasPensieveBadge(stats: PensieveStats | undefined): boolean {
  if (!stats) return false;
  return stats.positiveSignals >= PENSIEVE_MIN_POSITIVE_SIGNALS
    && stats.totalSamples >= PENSIEVE_MIN_SAMPLES;
}

// =============================================
// PHASE 5 SCORING — Impact (engagement depth + pensieve strength)
// =============================================

const IMPACT_BOOST_CAP = 1;
const IMPACT_COMPLETION_THRESHOLD = 2;
const IMPACT_PENSIEVE_POSITIVE_THRESHOLD = 3;

/**
 * Calculate Phase 5 impact boost from engagement depth + pensieve strength.
 *
 * This is a REFINEMENT LAYER only — max +1, never dominates.
 * Derived from already-fetched engagementMap + pensieveMap — zero extra queries.
 *
 * +0.5 if uniqueOpeners >= 3 AND completionCount >= 2 (high engagement depth)
 * +0.5 if positiveSignals >= 3 AND avgConfidence > 0 (consistent pensieve strength)
 * Cap at 1.
 *
 * ── CORE-AH: returns the composition, not just the sum ──
 *
 * The two halves have different OWNERS. Engagement depth is OptiBay's own
 * derivation over its own interaction data; pensieve strength is Catalyst
 * inference. The displayed explanation has to keep those apart, and the sum
 * cannot tell them apart once they are added.
 *
 * It is not recoverable downstream either. `scorePensieve` awards its points
 * from EITHER `totalSamples >= 5 AND avgConfidence > 0` OR
 * `positiveSignals >= 2`, while the learned half here needs
 * `positiveSignals >= 3 AND avgConfidence > 0`. So `pensieve > 0` does NOT
 * imply this half fired — a breakdown with 5 samples, 0.5 confidence and 0
 * positive signals carries `pensieve: 1` while `impact` is purely Core-owned.
 * Inferring the composition from the neighbouring field would mislabel that
 * case; reporting it is the only truthful option.
 *
 * `core + learned === total` and the cap applies to the sum exactly as before,
 * so the number this function contributes to the score is unchanged.
 */
function scoreImpact(
  engagement: EngagementStats | undefined,
  pensieve: PensieveStats | undefined
): { total: number; core: number; learned: number } {
  let core = 0;
  let learned = 0;

  // +0.5 for high engagement depth — Core-owned
  if (engagement &&
      engagement.uniqueOpeners >= UNIQUE_OPENER_THRESHOLD &&
      engagement.completionCount >= IMPACT_COMPLETION_THRESHOLD) {
    core += 0.5;
  }

  // +0.5 for consistent pensieve strength — Catalyst inference
  if (pensieve &&
      pensieve.positiveSignals >= IMPACT_PENSIEVE_POSITIVE_THRESHOLD &&
      pensieve.avgConfidence > 0) {
    learned += 0.5;
  }

  // The cap is applied to the SUM, exactly as before. Both halves are 0.5 and
  // the cap is 1, so it cannot bind today; it is kept so this function's
  // numeric contract is untouched. If a future half pushed the sum over the
  // cap, the parts are scaled together so `core + learned === total` holds.
  const uncapped = core + learned;
  const total = Math.min(uncapped, IMPACT_BOOST_CAP);
  if (uncapped > total && uncapped > 0) {
    const scale = total / uncapped;
    return { total, core: core * scale, learned: learned * scale };
  }

  return { total, core, learned };
}

// =============================================
// PHASE 6 SCORING — Vehicle Fitment (YMME)
// =============================================

const FITMENT_BOOST_CAP = 10;

/**
 * Calculate fitment boost for an artifact based on vehicle YMME match.
 *
 * Scoring:
 * - Exact year+make+model → +8 (or artifact's boost value, capped at 10)
 * - Make+model → +5
 * - Make only → +2
 * - Engine match adds +2 on top
 * - No fitment data → 0 (no penalty)
 */
function scoreFitment(
  artifactFitments: FitmentRow[] | undefined,
  context: TicketContext
): number {
  if (!artifactFitments?.length || !context.vehicleMake) return 0;

  let best = 0;

  for (const f of artifactFitments) {
    let score = 0;
    const makeMatch = f.make && context.vehicleMake?.toLowerCase() === f.make.toLowerCase();
    const modelMatch = f.model && context.vehicleModel?.toLowerCase() === f.model.toLowerCase();
    const yearMatch = f.year && context.vehicleYear === f.year;
    const engineMatch = f.engine && context.vehicleEngine?.toLowerCase().includes(f.engine.toLowerCase());

    if (yearMatch && makeMatch && modelMatch) {
      score = f.boost ?? 8;
    } else if (makeMatch && modelMatch) {
      score = 5;
    } else if (makeMatch) {
      score = 2;
    }

    if (engineMatch && score > 0) {
      score += 2;
    }

    if (score > best) best = score;
  }

  return Math.min(best, FITMENT_BOOST_CAP);
}

// =============================================
// SEMANTIC STUB (future)
// =============================================

/**
 * Compute semantic similarity score between artifact and context.
 *
 * STUB: Returns 0 until embedding infrastructure is available.
 * When implemented: simple cosine similarity, max +2, non-authoritative.
 * NEVER overrides job eligibility or evidence-based design principles.
 */
export function computeSemanticScore(
  _artifact: TrainingArtifact,
  _contextText: string
): number {
  return 0;
}

// =============================================
// SORT + EXPORT
// =============================================

/**
 * Score and sort artifacts by contextual relevance + engagement + impact + fitment.
 *
 * Sort: total score DESC → artifact_type ASC → title ASC
 * Stable sort: zero-score artifacts preserve Phase 1 order.
 *
 * Engagement stats scope is GLOBAL (all techs, all shops) by design —
 * stronger signal with current data volume.
 *
 * Max total score: Phase 2 (7) + Phase 3 (2) + Phase 4 (2) + Phase 5 (1) + Phase 6 (10) + Semantic (0) = 22
 */
export function scoreAndSort(
  artifacts: TrainingArtifact[],
  context: TicketContext,
  engagementMap?: Map<string, EngagementStats>,
  pensieveMap?: Map<string, PensieveStats>,
  fitmentMap?: Map<string, FitmentRow[]>
): { sorted: TrainingArtifact[]; scores: Map<string, ScoreBreakdown> } {
  const scores = new Map<string, ScoreBreakdown>();

  for (const art of artifacts) {
    const { category, keyword } = scoreContext(art, context);
    const engagementStats = engagementMap?.get(art.id);
    const pensieveStats = pensieveMap?.get(art.id);
    const engagement = scoreEngagement(engagementStats);
    const pensieve = scorePensieve(pensieveStats);
    const impactParts = scoreImpact(engagementStats, pensieveStats);
    const impact = impactParts.total;
    const fitment = scoreFitment(fitmentMap?.get(art.id), context);
    const semantic = computeSemanticScore(art, ''); // stub
    const total = category + keyword + engagement + pensieve + impact + fitment + semantic;

    scores.set(art.id, {
      category, keyword, engagement, pensieve, impact, fitment, semantic, total,
      // Provenance only. Not summed into `total`; see ScoreBreakdown.
      impactCore: impactParts.core,
      impactLearned: impactParts.learned,
    });
  }

  const sorted = [...artifacts].sort((a, b) => {
    // Primary: score descending
    const sa = scores.get(a.id)?.total ?? 0;
    const sb = scores.get(b.id)?.total ?? 0;
    if (sb !== sa) return sb - sa;

    // Secondary: artifact_type ascending (same as Phase 1)
    const typeCmp = a.artifact_type.localeCompare(b.artifact_type);
    if (typeCmp !== 0) return typeCmp;

    // Tertiary: title ascending (same as Phase 1)
    return a.title.localeCompare(b.title);
  });

  return { sorted, scores };
}

// =====================================================================
// Batch 18-G4 — Ticket-side tier-aware ranking helper
// =====================================================================
// G2 / G3 chose Option B (shared tier contract with surface-specific
// adapters). G4 is the first surface adoption: ticket-side
// recommendations layer a tier-aware shape on top of the existing
// `scoreAndSort` output without replacing it. Passport / Library /
// Browse / Advisor surfaces remain on their current paths until
// G7 / G8.
//
// The new shape lives in `src/types/trainingRecommendations.ts`. This
// helper is pure and decoupled from any specific data source — the
// caller (the future `useJobTrainingArtifacts` extension or a focused
// test) prepares candidates by combining `scoreAndSort` output with
// requirement-match data and completion-summary data from the existing
// hooks. G4 does NOT extend `useJobTrainingArtifacts` yet; the brief
// explicitly allows deferring the data bridge ("If `useJobTrainingArtifacts`
// does not know org/shop/role/team/domain/technician enough to safely
// match requirements, do not fake it. Instead: implement type file +
// scorer support.").
// =====================================================================

import type {
  RankedTrainingRecommendation,
  TrainingRecommendationCandidate,
  TrainingRecommendationContext,
  TrainingRecommendationReason,
  TrainingRecommendationReasonCode,
  TrainingRecommendationRequirementMatch,
  TrainingRecommendationTier,
} from '@/types/trainingRecommendations';

/**
 * Numeric weights per tier — tier outranks score, so any
 * `'required_policy'` candidate sorts above any
 * `'exact_ticket_match'` candidate regardless of intra-tier numeric
 * score. Scores break ties within a tier.
 */
const TIER_WEIGHTS: Record<TrainingRecommendationTier, number> = {
  required_policy: 1_000_000,
  exact_ticket_match: 100_000,
  technician_improvement: 10_000,
  related_helpful: 1_000,
  general_fallback: 100,
};

/**
 * Build the structured `reasons` array for a candidate. Order is
 * stable so source-pins and behaviour tests can rely on it. Codes are
 * deduplicated per candidate.
 */
export function buildTrainingRecommendationReasons(
  candidate: TrainingRecommendationCandidate,
  options?: {
    baseTier?: TrainingRecommendationTier;
    matchedJobName?: string;
    matchedSystem?: string;
  },
): TrainingRecommendationReason[] {
  const reasons: TrainingRecommendationReason[] = [];
  const seen = new Set<TrainingRecommendationReasonCode>();
  const push = (
    code: TrainingRecommendationReasonCode,
    weightClass: TrainingRecommendationReason['weightClass'],
    data?: TrainingRecommendationReason['data'],
  ) => {
    if (seen.has(code)) return;
    seen.add(code);
    reasons.push({ code, weightClass, data });
  };

  const matches = candidate.requirementMatches ?? [];
  if (matches.length > 0) {
    push('required_by_shop', 'policy', { requirementId: matches[0].requirementId });
    if (matches.some((m) => m.requiresManagerVerification === true)) {
      push('manager_verification_required', 'policy');
    }
    if (matches.some((m) => m.isOverdue === true)) {
      push('overdue', 'strong');
    } else if (matches.some((m) => m.isDueSoon === true)) {
      push('due_soon', 'strong');
    }
  }

  if (options?.baseTier === 'exact_ticket_match' || candidate.fitment?.fitsYearMakeModelEngine) {
    if (options?.matchedJobName) {
      push('job_match', 'strong', { matchedJob: options.matchedJobName });
    }
    if (options?.matchedSystem) {
      push('system_match', 'strong', { system: options.matchedSystem });
    }
  }

  const summary = candidate.completionSummary;
  if (summary && (summary.status === 'manual_submitted' || summary.status === 'verified' || summary.status === 'verification_pending')) {
    push('previously_completed', 'info', {
      lastCompletedAt: summary.lastCompletedAt ?? null,
    });
  }

  return reasons;
}

/**
 * Resolve the tier for a single candidate. Tier rules from G2 §5 /
 * G3 §4:
 *   - Any active requirement match → `'required_policy'`.
 *   - Else if the caller passed `baseTier`, use it (allowing the
 *     job-driven scorer to declare `'exact_ticket_match'` /
 *     `'related_helpful'` / `'general_fallback'` based on its own
 *     signals).
 *   - Else default to `'general_fallback'`.
 */
export function resolveTrainingRecommendationTier(
  candidate: TrainingRecommendationCandidate,
  baseTier?: TrainingRecommendationTier,
): TrainingRecommendationTier {
  if ((candidate.requirementMatches?.length ?? 0) > 0) {
    return 'required_policy';
  }
  return baseTier ?? 'general_fallback';
}

/**
 * Resolve the badges object for a candidate. Badges are pre-computed
 * so surface adapters are read-only over them; they reflect the
 * F32/F36 helper precedence rules.
 */
export function resolveTrainingRecommendationBadges(
  candidate: TrainingRecommendationCandidate,
): RankedTrainingRecommendation['badges'] {
  const matches = candidate.requirementMatches ?? [];
  const summary = candidate.completionSummary;
  const required = matches.length > 0;
  const managerVerificationRequired =
    matches.some((m) => m.requiresManagerVerification === true);

  return {
    required,
    recommended: !required, // The other tiers all surface as "recommended" rather than "required".
    managerVerificationRequired,
    alreadyCompleted:
      summary?.status === 'manual_submitted' || summary?.status === 'verified',
    verificationPending: summary?.status === 'verification_pending',
    verified: summary?.status === 'verified',
    revoked: summary?.status === 'revoked',
  };
}

/**
 * Pure builder for a single `RankedTrainingRecommendation`. The
 * caller supplies the numeric within-tier score (typically from
 * `scoreAndSort`'s `ScoreBreakdown.total`), the base tier (the
 * job/fitment scorer's verdict), and optional reason hints.
 *
 * Tier resolution always wins: an active requirement match elevates
 * the candidate to `'required_policy'` regardless of `baseTier`.
 *
 * `dedupeKey` defaults to the artifact id; the caller may supply a
 * content-cluster key once one exists (G6 audits).
 */
export function buildRankedTrainingRecommendation(args: {
  candidate: TrainingRecommendationCandidate;
  intraTierScore: number;
  baseTier?: TrainingRecommendationTier;
  matchedJobName?: string;
  matchedSystem?: string;
  dedupeKey?: string;
}): RankedTrainingRecommendation {
  const { candidate, intraTierScore, baseTier, matchedJobName, matchedSystem, dedupeKey } = args;
  const tier = resolveTrainingRecommendationTier(candidate, baseTier);
  const reasons = buildTrainingRecommendationReasons(candidate, {
    baseTier: tier,
    matchedJobName,
    matchedSystem,
  });
  const badges = resolveTrainingRecommendationBadges(candidate);

  // Tier wins; intra-tier score (clamped to [0, 99_999]) breaks ties.
  const safeScore = Math.max(0, Math.min(99_999, intraTierScore));
  const score = TIER_WEIGHTS[tier] + safeScore;

  const visibility: RankedTrainingRecommendation['visibility'] | undefined =
    badges.revoked ? { suppressCompleteDisplay: true } : undefined;

  return {
    candidate,
    tier,
    score,
    reasons,
    badges,
    dedupeKey: dedupeKey ?? candidate.artifactId,
    visibility,
  };
}

/**
 * Sort a batch of pre-built ranked recommendations. The pure
 * comparator delegates entirely to the numeric `score` field, which
 * already encodes tier precedence via `TIER_WEIGHTS`.
 *
 * Stable: when two candidates score identically the input order is
 * preserved so the ticket UI keeps its existing ordering on ties.
 */
export function sortRankedTrainingRecommendations(
  ranked: RankedTrainingRecommendation[],
): RankedTrainingRecommendation[] {
  return [...ranked].sort((a, b) => b.score - a.score);
}

/**
 * Convenience wrapper: takes candidates + intra-tier scores + base
 * tiers and returns a sorted `RankedTrainingRecommendation[]`. The
 * `context` argument is kept on the surface even though this MVP
 * doesn't consume it directly — it pins the contract for future G5+
 * surface adapters and is what the ticket-side adapter will pass
 * through.
 */
export function rankTicketTrainingRecommendations(
  context: TrainingRecommendationContext,
  inputs: Array<{
    candidate: TrainingRecommendationCandidate;
    intraTierScore: number;
    baseTier?: TrainingRecommendationTier;
    matchedJobName?: string;
    matchedSystem?: string;
  }>,
): RankedTrainingRecommendation[] {
  void context; // reserved for G5+ adapter use; G4 keeps the surface for future-friendliness.
  const ranked = inputs.map((input) => buildRankedTrainingRecommendation(input));
  return sortRankedTrainingRecommendations(ranked);
}

/**
 * Light helper to convert the existing `TicketContext` + `FitmentRow`
 * shape into a partial `TrainingRecommendationCandidate`. Used by the
 * ticket data adapter when it lands in G5+. Re-exported here so a
 * single import path serves both the scorer and the new contract.
 *
 * G4 ships only the shape; G5+ wires the data layer.
 */
export function buildTicketTrainingRecommendationCandidate(args: {
  artifactId: string;
  title: string;
  isActive: boolean;
  description?: string | null;
  categoryKey?: string | null;
  domainKeys?: string[];
  systemKeys?: string[];
  jobIds?: string[];
  jobCodes?: string[];
  requirementMatches?: TrainingRecommendationRequirementMatch[];
  completionSummary?: TrainingRecommendationCandidate['completionSummary'];
  engagementSignals?: TrainingRecommendationCandidate['engagementSignals'];
  fitment?: TrainingRecommendationCandidate['fitment'];
  source?: TrainingRecommendationCandidate['source'];
}): TrainingRecommendationCandidate {
  return {
    artifactId: args.artifactId,
    title: args.title,
    description: args.description,
    categoryKey: args.categoryKey,
    domainKeys: args.domainKeys,
    systemKeys: args.systemKeys,
    jobIds: args.jobIds,
    jobCodes: args.jobCodes,
    isActive: args.isActive,
    requirementMatches: args.requirementMatches,
    completionSummary: args.completionSummary,
    engagementSignals: args.engagementSignals,
    fitment: args.fitment,
    source: args.source,
  };
}
